import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { formatVnd } from "../../shared/i18n/index.ts";
import { BUY_MAX_UNITS } from "../../shared/limits.ts";
import type { Segment } from "../../shared/price.ts";
import { ApiError } from "../lib/api.ts";
import { serverNow } from "../lib/clock.ts";
import { interpolate } from "../../shared/price.ts";
import { useI18n } from "../lib/i18n.tsx";
import { useMe } from "../lib/me.tsx";
import { buy } from "../lib/purchase-api.ts";

/** Buy sheet (08-design.md §Buy sheet): quantity, current price, total, the "you pay the lower one" note. */
export function BuySheet({
  listingId,
  segment,
  onClose,
}: {
  listingId: string;
  segment: Segment;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { me } = useMe();
  const navigate = useNavigate();
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = useMemo(() => crypto.randomUUID(), []);
  const max = Math.max(1, Math.min(BUY_MAX_UNITS, segment.qty_remaining));
  const current = interpolate(segment, serverNow());

  async function confirm() {
    if (!me) {
      navigate(`/login?next=/l/${listingId}`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const p = await buy(listingId, qty, key);
      navigate(`/p/${p.id}?bought=1`);
    } catch (e) {
      const code = e instanceof ApiError ? e.body.error : "network";
      const map: Record<string, Parameters<typeof t>[0]> = {
        sold_out: "buy.sold_out",
        ended: "buy.ended",
        own_listing: "buy.own_listing",
        open_cap: "buy.open_cap",
        open_problem: "buy.open_problem",
        suspended: "error.suspended",
        unauthorized: "buy.login_required",
      };
      setError(t(map[code] ?? "error.generic"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sheet" role="dialog" aria-label={t("buy.cta")} data-testid="buy-sheet">
      {max > 1 ? (
        <label className="field">
          <span className="label">{t("buy.quantity")}</span>
          <div className="row">
            <button
              className="btn"
              type="button"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              aria-label="−"
            >
              −
            </button>
            <strong data-testid="buy-qty">{qty}</strong>
            <button
              className="btn"
              type="button"
              onClick={() => setQty((q) => Math.min(max, q + 1))}
              aria-label="+"
            >
              +
            </button>
          </div>
        </label>
      ) : null}
      <p>
        {t("price.current")}: <strong>{formatVnd(current)}</strong>
      </p>
      <p>
        {t("buy.total")}: <strong data-testid="buy-total">{formatVnd(current * qty)}</strong>
      </p>
      <p className="muted small">{t("buy.confirm.note")}</p>
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="row">
        <button
          className="btn btn--accent"
          disabled={busy}
          onClick={() => void confirm()}
          data-testid="buy-confirm"
        >
          {t("buy.confirm.cta")}
        </button>
        <button className="btn btn--ghost" onClick={onClose}>
          {t("common.cancel")}
        </button>
      </div>
    </div>
  );
}
